import { useCallback } from 'react';

// Helper function to compare arrays
function arraysEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // Sort arrays with explicit comparison function
  const aSorted = [...a].sort((x, y) => String(x).localeCompare(String(y)));
  const bSorted = [...b].sort((x, y) => String(x).localeCompare(String(y)));

  for (let i = 0; i < aSorted.length; ++i) {
    if (aSorted[i] !== bSorted[i]) return false;
  }
  return true;
}

// Check if a multiple choice answer is correct
const checkMultipleChoice = (question, userAnswer) => {
  const correctAnswers = question.options
    .filter(opt => opt.isCorrect)
    .map(opt => opt.id.toString());
  
  if (question.multiple) {
    if (!Array.isArray(userAnswer)) return false;
    return arraysEqual(
      [...userAnswer].map(String).sort((a, b) => a.localeCompare(b)), 
      [...correctAnswers].sort((a, b) => a.localeCompare(b))
    );
  }
  return correctAnswers.includes(String(userAnswer));
};

// Check if a match answer is correct
const checkMatchAnswer = (question, userAnswer) => {
  try {
    if (!userAnswer || !Array.isArray(question.correctMatches)) return false;
    
    // Handle case where userAnswer might be an array of matches or a single match
    const userMatches = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
    
    // Convert both user matches and correct matches to a consistent format for comparison
    const normalizeMatch = (match) => {
      if (!match) return null;
      // Handle different possible match formats
      if (match.leftId !== undefined && match.rightId !== undefined) {
        return `${match.leftId}-${match.rightId}`;
      }
      if (match.left !== undefined && match.right !== undefined) {
        return `${match.left}-${match.right}`;
      }
      if (Array.isArray(match) && match.length === 2) {
        return `${match[0]}-${match[1]}`;
      }
      return null;
    };
    
    const userMatchStrings = userMatches
      .map(normalizeMatch)
      .filter(Boolean) // Remove any null/undefined values
      .sort((a, b) => a.localeCompare(b));
      
    const correctMatchStrings = question.correctMatches
      .map(match => `${match.leftId}-${match.rightId}`)
      .sort((a, b) => a.localeCompare(b));
    
    console.log('Match validation:', {
      userMatches,
      userMatchStrings,
      correctMatchStrings,
      isEqual: userMatchStrings.length === correctMatchStrings.length &&
               userMatchStrings.every((match, i) => match === correctMatchStrings[i])
    });
    
    return userMatchStrings.length === correctMatchStrings.length &&
           userMatchStrings.every((match, i) => match === correctMatchStrings[i]);
  } catch (error) {
    console.error('Error in checkMatchAnswer:', error);
    return false;
  }
};

// Check if an arrange answer is correct
const checkArrangeAnswer = (question, userAnswer) => {
  if (!Array.isArray(userAnswer) || !Array.isArray(question.correctOrder)) return false;
  if (userAnswer.length !== question.correctOrder.length) return false;
  
  return userAnswer.every((item, index) => {
    if (typeof item === 'object' && item !== null) {
      return String(item.id) === String(question.correctOrder[index]);
    }
    return String(item) === String(question.correctOrder[index]);
  });
};

// Check if an answer is correct for a question
const isAnswerCorrect = (question, userAnswer) => {
  if (!userAnswer) return false;
  
  switch (question.type) {
    case 'multiple':
      return checkMultipleChoice(question, userAnswer);
    case 'match':
      return checkMatchAnswer(question, userAnswer);
    case 'arrange':
      return checkArrangeAnswer(question, userAnswer);
    default:
      return false;
  }
};

// Main hook for answer validation
const useAnswerValidation = (currentQuestion, userAnswers) => {
  // Check if the current answer is correct
  const validateAnswer = useCallback(() => {
    if (!currentQuestion) return false;
    return isAnswerCorrect(currentQuestion, userAnswers?.[currentQuestion.id]);
  }, [currentQuestion, userAnswers]);

  return { isAnswerCorrect: validateAnswer };
};

export { isAnswerCorrect };
export default useAnswerValidation;
